import * as React from "react"
import { cva } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"

// ============================================================================
// Toast Types
// ============================================================================

export type ToastType = "default" | "success" | "error" | "warning" | "info"

export interface Toast {
  id: string
  title?: string
  description?: string
  type?: ToastType
  duration?: number
  action?: React.ReactNode
}

export interface ToastState {
  toasts: Toast[]
}

// ============================================================================
// Toast Context
// ============================================================================

type ToastAction =
  | { type: "ADD_TOAST"; toast: Toast }
  | { type: "REMOVE_TOAST"; id: string }

const ToastContext = React.createContext<{
  toasts: Toast[]
  toast: (options: Omit<Toast, "id">) => string
  dismiss: (id: string) => void
} | null>(null)

function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case "ADD_TOAST":
      return { toasts: [...state.toasts, action.toast] }
    case "REMOVE_TOAST":
      return { toasts: state.toasts.filter((t) => t.id !== action.id) }
    default:
      return state
  }
}

let toastCount = 0

// ============================================================================
// Toast Provider
// ============================================================================

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(toastReducer, { toasts: [] })

  const toast = React.useCallback((options: Omit<Toast, "id">) => {
    const id = `toast-${++toastCount}`
    const duration = options.duration ?? 5000

    dispatch({ type: "ADD_TOAST", toast: { id, ...options } })

    if (duration > 0) {
      setTimeout(() => {
        dispatch({ type: "REMOVE_TOAST", id })
      }, duration)
    }

    return id
  }, [])

  const dismiss = React.useCallback((id: string) => {
    dispatch({ type: "REMOVE_TOAST", id })
  }, [])

  return (
    <ToastContext.Provider value={{ toasts: state.toasts, toast, dismiss }}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  )
}

// ============================================================================
// useToast Hook
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider")
  }

  return {
    toast: context.toast,
    dismiss: context.dismiss,
    toasts: context.toasts,
    // Convenience methods
    success: (title: string, description?: string) =>
      context.toast({ title, description, type: "success" }),
    error: (title: string, description?: string) =>
      context.toast({ title, description, type: "error" }),
    warning: (title: string, description?: string) =>
      context.toast({ title, description, type: "warning" }),
    info: (title: string, description?: string) =>
      context.toast({ title, description, type: "info" }),
  }
}

// ============================================================================
// Toast Variants
// ============================================================================

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full",
  {
    variants: {
      type: {
        default: "border-border bg-background text-foreground",
        success: "border-success/50 bg-success/10 text-success dark:bg-success/20",
        error: "border-destructive/50 bg-destructive/10 text-destructive dark:bg-destructive/20",
        warning: "border-warning/50 bg-warning/10 text-warning dark:bg-warning/20",
        info: "border-primary/50 bg-info-light text-info",
      },
    },
    defaultVariants: {
      type: "default",
    },
  }
)

// ============================================================================
// Toast Component
// ============================================================================

interface ToastItemProps extends Toast {
  onDismiss: (id: string) => void
}

function ToastItem({ id, title, description, type = "default", action, onDismiss }: ToastItemProps) {
  return (
    <div
      className={cn(toastVariants({ type }))}
      data-state="open"
    >
      <div className="grid gap-1">
        {title && <div className="text-sm font-semibold">{title}</div>}
        {description && <div className="text-sm opacity-90">{description}</div>}
      </div>
      {action}
      <button
        onClick={() => onDismiss(id)}
        className="absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 group-hover:opacity-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

// ============================================================================
// Toaster (renders all toasts)
// ============================================================================

function Toaster() {
  const context = React.useContext(ToastContext)
  if (!context) return null

  const { toasts, dismiss } = context

  return (
    <div className="fixed top-4 right-4 z-[100] flex max-h-screen w-full max-w-[420px] flex-col gap-2 p-4 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} {...toast} onDismiss={dismiss} />
      ))}
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Toaster, toastVariants }




